import { Component, computed, effect, inject, ElementRef, HostListener, AfterViewInit, viewChild } from '@angular/core';
import { IconComponent } from '../icons/icon.component';
import { A11yModule } from '@angular/cdk/a11y';
import { IconNameType } from '../icons/icon-name-type.enum';
import { ModalName, ModalService } from '../../core/services/modal.service';
import { Fundraiser } from '../../core/models/ordering/fundraiser.model';
import { Sponsor } from '../../core/models/ordering/sponsor.model';
import { FundraiserSponsorEntryComponent } from './fundraiser-sponsor-entry/fundraiser-sponsor-entry.component';
import { AsyncButtonDirective } from '../directives/async-button.directive';
import { Subscription } from 'rxjs';
import { SpecialInstructionsAccordionComponent } from "../special-instructions-accordion/special-instructions-accordion.component";
import { OrderedFundraiser } from '../../core/models/ordering/ordered-fundraiser.model';
import { OrderedSponsor } from '../../core/models/ordering/ordered-sponsor.model';
import { OrderFundraiserService } from '../../core/services/ordering/order-fundraiser.service';
import { OrderService } from '../../core/services/order.service';

@Component({
  selector: 'cwo-fundraiser-sponsors-modal',
  standalone: true,
  imports: [
    IconComponent,
    A11yModule,
    FundraiserSponsorEntryComponent,
    AsyncButtonDirective,
    SpecialInstructionsAccordionComponent
],
  templateUrl: './fundraiser-sponsors-modal.component.html',
  styleUrl: './fundraiser-sponsors-modal.component.scss'
})
export class FundraiserSponsorsModalComponent implements AfterViewInit {
  readonly modalContainer = viewChild('modalContainer', { read: ElementRef });

  modalTitleId = 'fundraiser-modal-title-' + Math.random().toString(36).slice(2, 9);
  modalDescId = 'fundraiser-modal-desc-' + Math.random().toString(36).slice(2, 9);
  requiredError: boolean = false;
  
  ngAfterViewInit(): void {
    try {
      const el = this.modalContainer()?.nativeElement as HTMLElement | undefined;
      if (el) {
        const firstFocusable = el.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') as HTMLElement | null;
        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          el.focus();
        }
      }
    } catch (e) {
      // no-op
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.closeModal();
  }
  private modalService = inject(ModalService);
  private orderService = inject(OrderService);
  private orderFundraiserService = inject(OrderFundraiserService);
  iconNameType = IconNameType;

  constructor() {
    effect(() => {
      const isFundraiserApplied = this.isFundraiserApplied();

      if(isFundraiserApplied) {
        const order = this.orderService.order();
        const fundraiser = this.fundraiser();

        const raw = order.fundraisers?.find((f: OrderedFundraiser) => f.name === fundraiser.name) as any;
        if (raw) {
          const deserialized = new OrderedFundraiser().deserialize(raw);
          deserialized.sponsors = (raw.sponsors || []).map((s: any) => new OrderedSponsor().deserialize(s));
          this.selectedSponsors = deserialized.sponsors ?? [];
          this.orderedFundraiser = deserialized;
        }
      }
    })
  }

  orderedFundraiser: OrderedFundraiser | undefined = undefined;
  isFundraiserApplied = computed(() => {
    const order = this.orderService.order();
    const fundraiser = this.fundraiser();

    if(!order || !fundraiser) return false;

    const isFundraiserApplied = !!order.fundraisers?.some((f: OrderedFundraiser) => f.name === fundraiser.name);
    return !!isFundraiserApplied;
  });

  selectedSponsors: OrderedSponsor[] = [];
  loadingSubscription: Subscription | null = null;
  fundraiser = computed<Fundraiser>(() => {
    const modalState = this.modalService.stateForModal(ModalName.FundraiserSponsors);
    return modalState;
  });

  onSponsorToggled(event: { sponsor: Sponsor; selected: boolean }) {
    this.resetErrors();
    if (event.selected) {
      const ordered = OrderedSponsor.fromSponsor(event.sponsor);
      if (!this.selectedSponsors.some(s => s.name === ordered.name)) {
        this.selectedSponsors = [...this.selectedSponsors, ordered];
      }
    } else {
      this.selectedSponsors = this.selectedSponsors.filter(s => s.name !== event.sponsor.name);
    }
  }

  closeModal() {
    this.modalService.closeModal(ModalName.FundraiserSponsors);
  }

  addToOrder() {
    const sponsors = this.selectedSponsors;
    const orderedFundraiser = OrderedFundraiser.fromFundraiser(this.fundraiser());
    orderedFundraiser.sponsors = sponsors;

    if(this.fundraiser()?.requireSponsorSelection && !(sponsors.length > 0)) {
      this.setErrors();
      return;
    } else {
      this.resetErrors();
    }

    this.loadingSubscription = this.orderFundraiserService.addFundraiserToOrder(orderedFundraiser).subscribe({
      next: () => {
        this.closeModal();
        this.loadingSubscription = null;
      }, error: () => {
        this.loadingSubscription = null;
      }
    })
  }

  onInstructionsChange(val: any, sponsor: Sponsor) {
    const selectedSponsor = this.findOrderedSponsor(sponsor);

    if(!selectedSponsor) return;

    selectedSponsor.notes = val;
  }

  existsInSelectedSponsors(sponsor: Sponsor): boolean {
    return this.selectedSponsors.some(s => s.name === sponsor.name);
  }

  private findOrderedSponsor(sponsor: Sponsor) {
    return this.selectedSponsors.find(sp => sp.name === sponsor.name);
  }

  selectedSponsorNotes(sponsor: Sponsor): string {
    const ordered = this.findOrderedSponsor(sponsor);
    return ordered?.notes ?? '';
  }

  updateFundraiser() {
    const sponsors = this.selectedSponsors;
    const orderedFundraiser = this.orderedFundraiser;

    if(this.fundraiser()?.requireSponsorSelection && !(sponsors.length > 0)) {
      this.setErrors();
    } else {
      this.resetErrors();
    }

    if(orderedFundraiser) {
      orderedFundraiser.sponsors = sponsors;
  
      this.loadingSubscription = this.orderFundraiserService.updateFundraiser(orderedFundraiser).subscribe({
        next: () => {
          this.closeModal();
          this.loadingSubscription = null;
        }, error: () => {
          this.loadingSubscription = null;
        }
      })
    } 
  }

  resetErrors() {
    this.requiredError = false;
  }

  setErrors() {
    this.requiredError = true;
  }
}
